import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../config/URL";
import {
  ThemeProvider,
  createTheme,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import GlobalDelete from "../../components/common/GlobalDelete";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import { exportVisibleColumnsToCSV } from "../../components/common/ExportCsv";
import {
  getColumnPreference,
  saveColumnPreference,
} from "../../utility/mrtColumnPersistence";

const MODULE_KEY = "COURSE";

const Course = ({ selectedCenter }) => {
  const [filters, setFilters] = useState({
    centerId: selectedCenter,
    courseName: "",
    courseCode: "",
  });

  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const storedScreens = JSON.parse(localStorage.getItem("screens") || "{}");
  // const canIndex = Boolean(storedScreens?.courseIndex);
  const canRead = Boolean(storedScreens?.courseRead);
  const canCreate = Boolean(storedScreens?.courseCreate);
  const canUpdate = Boolean(storedScreens?.courseUpdate);
  const canDelete = Boolean(storedScreens?.courseDelete);
  const canCourseFees = Boolean(storedScreens?.courseFeesIndex);
  const canCourseDepositFees = Boolean(storedScreens?.courseDepositFeesIndex);
  const canCurriculumOutline = Boolean(storedScreens?.curriculumOutlineIndex);
  const showActionColumn = canUpdate || canDelete;

  // const centerLocalId = localStorage.getItem("selectedCenterId");
  // const [centerData, setCenterData] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [isClearFilterClicked, setIsClearFilterClicked] = useState(false);

  const defaultColumnVisibility = {
    colorCode: false,
    description: false,
    durationInHrs: false,
    durationInMins: false,
    replacementLessonStudentBuffer: false,
    classReplacementAllowed: false,
    createdBy: false,
    createdAt: false,
    updatedBy: false,
    updatedAt: false,
  };

  const [columnVisibility, setColumnVisibility] = useState(() =>
    getColumnPreference(MODULE_KEY, defaultColumnVisibility),
  );

  /* -------------------- COLUMNS -------------------- */
  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorFn: (row, index) => index + 1,
        header: "S.NO",
        size: 40,
        Cell: ({ cell }) => (
          <span style={{ textAlign: "center" }}>{cell.getValue()}</span>
        ),
      },
      {
        accessorFn: (row) => row.centerName[0],
        header: "Center Name",
      },
      { accessorKey: "levelName", header: "Level" },
      { accessorKey: "courseName", header: "Course Name" },
      { accessorKey: "courseCode", header: "Course Code" },
      { accessorKey: "subjectName", header: "Subject" },
      { accessorKey: "courseType", header: "Course Type" },
      { accessorKey: "minClassSize", header: "Min Class Size" },
      { accessorKey: "maxClassSize", header: "Max Class Size" },
      { accessorKey: "status", header: "Status" },
      { accessorKey: "colorCode", header: "Color Code" },
      { accessorKey: "description", header: "Description" },
      { accessorKey: "durationInHrs", header: "Duration In Hrs" },
      { accessorKey: "durationInMins", header: "Duration In Mins" },
      {
        accessorKey: "replacementLessonStudentBuffer",
        header: "Replacement Lesson",
      },
      {
        accessorKey: "classReplacementAllowed",
        header: "Class Replacement Allowed",
        Cell: ({ row }) =>
          row.original.classReplacementAllowed ? "Yes" : "No",
      },
      { accessorKey: "createdBy", header: "Created By" },
      {
        accessorKey: "createdAt",
        header: "Created On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10) || "",
      },
      {
        accessorKey: "updatedBy",
        header: "Updated By",
        Cell: ({ cell }) => cell.getValue() || "",
      },
    ];

    if (!showActionColumn) return baseColumns;

    const actionColumn = {
      id: "actions",
      header: "",
      size: 20,
      enableSorting: false,
      enableColumnActions: true,
      Cell: ({ row }) => (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
            setSelectedId(row.original.id);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    };

    return [
      baseColumns[0], // S.NO
      actionColumn, // Actions
      ...baseColumns.slice(1),
    ];
  }, [showActionColumn]);

  /* -------------------- FETCH DATA -------------------- */
  const getData = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();

      filters.centerId = selectedCenter;
      // Loop through other filters and add key-value pairs if they have a value
      for (let key in filters) {
        if (filters[key]) {
          queryParams.append(key, filters[key]);
        }
      }
      const endpoint = `/getCourseWithCustomInfo?${queryParams.toString()}`;
      const response = await api.get(endpoint);
      // const response = await api.get(`/getCourseWithCustomInfo?${queryParams}`);
      setData(response.data);
      setLoading(false);
    } catch (error) {
      toast.error(`Error Fetching Data: ${error.message}`);
    } finally {
      // setLoading(false);
      setIsClearFilterClicked(false);
    }
  };

  useEffect(() => {
    getData();
  }, [filters, selectedCenter]);

  const clearFilter = () => {
    setFilters({
      centerId: selectedCenter,
      courseName: "",
      courseCode: "",
    });
    setIsClearFilterClicked(true);
  };

  const handleMenuClose = () => setMenuAnchor(null);

  /* -------------------- TABLE INSTANCE -------------------- */
  const table = useMaterialReactTable({
    columns,
    data,
    state: {
      columnVisibility,
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility =
        typeof updater === "function" ? updater(columnVisibility) : updater;

      setColumnVisibility(newVisibility);
      saveColumnPreference(MODULE_KEY, newVisibility);
    },
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <button
        className="btn btn-sm btn-border"
        onClick={() => exportVisibleColumnsToCSV(table, data, "CourseList.csv")}
      >
        Export CSV
      </button>
    ),
    muiTableBodyRowProps: ({ row }) => ({
      onClick: canRead
        ? () => navigate(`/course/view/${row.original.id}`)
        : undefined,
      style: { cursor: canRead ? "pointer" : "default" },
    }),
  });

  /* -------------------- THEME -------------------- */
  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
    },
  });
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({ ...prevFilters, [name]: value }));
  };

  useEffect(() => {
    getData();
  }, [filters, selectedCenter]);

  return (
    <div className="container-fluid my-4 center">
      <ol
        className="breadcrumb my-3 px-1"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            &nbsp;Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          &nbsp;Course Management
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          &nbsp;Course
        </li>
      </ol>

      <div className="card">
        <div
          className="mb-3 d-flex justify-content-between align-items-center p-1"
          style={{ background: "#f5f7f9" }}
        >
          <div className="d-flex align-items-center">
            <div className="d-flex">
              <div className="dot active"></div>
            </div>
            <span className="me-2 text-muted">
              This database shows the list of
              <span className="bold" style={{ color: "#287f71" }}>
                Course
              </span>
            </span>
          </div>
        </div>
        {/* -------------------- FILTERS -------------------- */}
        <div className="d-flex justify-content-between mb-3 px-2">
          <div className="individual_fliters d-lg-flex">
            <div className="form-group mb-0 mb-1">
              <input type="hidden" name="centerId" value={filters.centerId} />

              {/* <select
                className="form-select form-select-sm center_list"
                style={{ width: "100%" }}
                onChange={(e) => {
                  const { value } = e.target;
                  setFilters((prevFilters) => ({
                    ...prevFilters,
                    centerId: value,
                  }));
                  setCenterId(value);
                }}
                name="centerId"
                value={filters.centerId}
              >
                <option value="">All Center</option>
                {centerData?.map((center) => (
                  <option key={center.id} value={center.id}>
                    {center.centerNames}
                  </option>
                ))}
              </select> */}
            </div>
            <div className="form-group mb-0 ms-2 mb-1">
              <input
                type="text"
                name="courseName"
                value={filters.courseName}
                onChange={handleFilterChange}
                className="form-control form-control-sm center_list"
                style={{ width: "160px" }}
                placeholder="Course"
                autoComplete="off"
              />
            </div>
            <div className="form-group mb-0 ms-2 mb-1">
              <input
                type="text"
                className="form-control form-control-sm center_list"
                style={{ width: "160px" }}
                placeholder="Code"
                name="courseCode"
                value={filters.courseCode}
                onChange={handleFilterChange}
                autoComplete="off"
              />
            </div>
            <div className="form-group mb-0 ms-2 mb-1">
              <button
                type="button"
                className="btn btn-sm btn-border"
                onClick={clearFilter}
              >
                Clear
              </button>
            </div>
          </div>
          {canCreate && (
            <Link to="/course/add">
              <button
                type="button"
                className="btn btn-button btn-sm me-2"
                style={{ fontWeight: "600" }}
              >
                &nbsp; Add &nbsp;&nbsp; <i className="bx bx-plus"></i>
              </button>
            </Link>
          )}
        </div>

        {/* -------------------- TABLE -------------------- */}
        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <>
            <ThemeProvider theme={theme}>
              <MaterialReactTable table={table} />
            </ThemeProvider>

            <Menu
              id="action-menu"
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
            >
              {canCourseFees && (
                <MenuItem
                  onClick={() => navigate(`/course/coursefees/${selectedId}`)}
                  className="text-start mb-0 menuitem-style"
                >
                  Course Fees
                </MenuItem>
              )}

              {canCourseDepositFees && (
                <MenuItem
                  onClick={() =>
                    navigate(`/course/coursedeposit/${selectedId}`)
                  }
                  className="text-start mb-0 menuitem-style"
                >
                  Course Deposit Fees
                </MenuItem>
              )}

              {canCurriculumOutline && (
                <MenuItem
                  onClick={() =>
                    navigate(`/course/curriculumoutlet/${selectedId}`)
                  }
                  className="text-start mb-0 menuitem-style"
                >
                  Curriculum Outlet
                </MenuItem>
              )}

              {/* <MenuItem onClick={() => navigate(`/course/view/${selectedId}`)}>
                View
              </MenuItem> */}
              {canUpdate && (
                <MenuItem
                  onClick={() => navigate(`/course/edit/${selectedId}`)}
                  className="text-start mb-0 menuitem-style"
                >
                  Edit
                </MenuItem>
              )}

              {canDelete && (
                <MenuItem>
                  <GlobalDelete
                    path={`/deleteCourse/${selectedId}`}
                    onOpen={handleMenuClose}
                    onDeleteSuccess={getData}
                  />
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </div>
    </div>
  );
};

export default Course;
